import React, { ComponentProps, use, useMemo } from "react";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { useTheme } from "../../hooks/use-theme";
import { View } from "react-native";
import { DrawerContentScrollView, createDrawerNavigator } from "@react-navigation/drawer";
import HStack from "../../ui_atoms/HStack";
import UserAvatar from "../../screens/user/UserAvatar";
import Text from "../../components/Text";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import TabBar from "../../components/TabBar";
import { XOR } from "ts-essentials";
import { WorkerTabParamList } from "../worker/Home";
import { ClientTabParamList } from "../client/Home";
import AppVersion from "../../components/AppVersion";
import Gap from "../../ui_atoms/Gap";
import ListItem from "../../ui_atoms/ListItem";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { ProfileType } from "../../__generated__/graphql";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import IconButton from "../../components/IconButton";

export type UnifiedTabParamList = XOR<WorkerTabParamList, ClientTabParamList>;

export const useNavigationNavigator = () => {
    const { fonts, marginSize } = useTheme();
    const { user } = use(AuthManagerContext);
    const { profileType, openProfileSwitch } = use(ProfileSwitcherManagerContext);
    const currentProfile = useCurrentProfile();
    const isLarge = useIsScreenSizeMin("large");
    const i18n = useI18n();

    const Navigator = useMemo(
        () =>
            (isLarge
                ? createDrawerNavigator
                : createBottomTabNavigator)<UnifiedTabParamList>() as ReturnType<
                typeof createBottomTabNavigator<UnifiedTabParamList>
            >,
        [isLarge],
    );

    const profileInfo = useMemo(() => {
        if (currentProfile) {
            if (profileType === ProfileType.Worker) {
                return <Text style={fonts.bodyMedium}>{i18n.t("profile.worker")}</Text>;
            } else if (profileType === ProfileType.Client) {
                if (currentProfile.isPrivateClient) {
                    return <Text style={fonts.bodyMedium}>{i18n.t("profile.private_client")}</Text>;
                }
                return (
                    <HStack alignment="center">
                        <UserAvatar size={24} avatarPath={currentProfile.client?.logoUrl} />
                        <Gap gap={0.2} />
                        <Text
                            style={[fonts.bodyMedium, { flexGrow: 1, flexShrink: 1 }]}
                            numberOfLines={1}
                        >
                            {currentProfile.client?.name || i18n.t("profile.client")}
                        </Text>
                    </HStack>
                );
            }
        }
        return <Text style={fonts.bodyMedium}>{i18n.t("loading")}</Text>;
    }, [currentProfile, fonts.bodyMedium, i18n, profileType]);

    return {
        NavigationNavigator: Navigator,
        navigatorProps: {
            ...(isLarge
                ? {
                      drawerContent: (props: ComponentProps<typeof TabBar>) => (
                          <DrawerContentScrollView
                              {...props}
                              contentContainerStyle={{ flexGrow: 1 }}
                          >
                              <HStack style={{ paddingLeft: 24, paddingVertical: 24 }}>
                                  <UserAvatar avatarPath={user?.avatarUrl} size={70} />
                                  <Gap gap="1/3" />
                                  <VStack style={{ flexGrow: 1, flexShrink: 1 }}>
                                      <Text style={[fonts.headlineSmall]} numberOfLines={1}>
                                          {user?.firstName
                                              ?.toLowerCase()
                                              .split(" ")
                                              .map(
                                                  (word) =>
                                                      word.charAt(0).toUpperCase() +
                                                      word.substring(1),
                                              )
                                              .join(" ")}
                                      </Text>
                                      {profileInfo}
                                  </VStack>
                                  <IconButton
                                      icon="chevron-down"
                                      size={24}
                                      onPress={openProfileSwitch}
                                  />
                              </HStack>
                              <TabBar {...props} />
                              <View style={{ flex: 1 }} />
                              <VStack gap={1}>
                                  {profileType === ProfileType.Worker && (
                                      <ListItem
                                          style={{ marginHorizontal: -marginSize / 2 }}
                                          rightActionMode="empty"
                                          labels={[
                                              {
                                                  text: i18n.t(`screens.invite_client.title`),
                                                  mode: "cta",
                                              },
                                          ]}
                                          onPress={() => {
                                              props.navigation.navigate("InviteClientInfo");
                                          }}
                                      />
                                  )}
                                  <AppVersion />
                              </VStack>
                              <Gap gap={0.5} />
                          </DrawerContentScrollView>
                      ),
                      screenOptions: { drawerType: "permanent" },
                  }
                : {}),
        },
    };
};
